// ── ChatWindow ─────────────────────────────────────────────────────
//
// Center pane. Header (contact + session badge + actions), scrolling
// message list, composer pinned to the bottom. Auto-marks the
// conversation as read 2s after open (gives the user time to scroll
// up if they want to see unread context first).

import { useEffect, useRef } from 'react';
import { ArrowLeft, ChevronRight, Inbox, Loader2 } from 'lucide-react';
import { Avatar } from './Avatar';
import { MessageComposer } from './MessageComposer';
import { MessageList } from './MessageList';
import { SessionBadge } from './SessionBadge';
import { useConversationDetail } from '../hooks/useConversationDetail';
import { markConversationRead } from '../api/whatsapp.api';

const MARK_READ_DELAY_MS = 2_000;

export function ChatWindow({
  conversationId,
  onBack,
  onToggleContactPanel,
  contactPanelOpen,
}: {
  conversationId: string | null;
  onBack?: () => void;
  onToggleContactPanel: () => void;
  contactPanelOpen: boolean;
}) {
  const { detail, loading, error, refresh } = useConversationDetail(conversationId);
  const markedRef = useRef<string | null>(null);

  // Auto mark-as-read 2s after opening (gives user time to scroll up).
  // Skip if there's nothing to mark.
  useEffect(() => {
    if (!detail || markedRef.current === detail.id) return;
    if (detail.unreadCount === 0) {
      markedRef.current = detail.id;
      return;
    }
    const t = window.setTimeout(async () => {
      try {
        await markConversationRead(detail.id, true);
        markedRef.current = detail.id;
        // Realtime emits wa:conversation:read which the list hook will
        // pick up — no manual refresh needed here.
      } catch {
        /* non-fatal — try again on next open */
      }
    }, MARK_READ_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [detail]);

  if (!conversationId) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-slate-50 px-6 text-center">
        <Inbox className="h-12 w-12 text-slate-300" />
        <p className="mt-3 text-base font-medium text-slate-700">Select a conversation</p>
        <p className="mt-1 text-sm text-slate-500 max-w-[280px]">
          Pick a thread from the left to start replying. New inbound messages appear here in real time.
        </p>
      </div>
    );
  }

  if (loading && !detail) {
    return (
      <div className="flex h-full items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 text-center">
        <p className="text-sm text-red-600">{error ?? 'Conversation not found'}</p>
        <button onClick={refresh} className="mt-2 text-xs text-indigo-600 hover:underline">
          Retry
        </button>
      </div>
    );
  }

  const displayName = detail.contactName || `+${detail.contactWaId}`;
  const phoneE164 = `+${detail.contactWaId}`;

  return (
    <div className="flex h-full w-full flex-col bg-white">
      {/* Header */}
      <header className="flex h-14 flex-shrink-0 items-center gap-3 border-b border-gray-100 px-3">
        {onBack && (
          <button
            onClick={onBack}
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 md:hidden"
            aria-label="Back to inbox"
            type="button"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        <Avatar name={displayName} size={36} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-slate-800">{displayName}</span>
            <SessionBadge
              lastInboundAt={detail.lastInboundAt}
              sessionExpiresAt={detail.sessionExpiresAt}
            />
          </div>
          <div className="truncate text-[11px] text-slate-500">
            {phoneE164}
            {detail.account?.verifiedName && (
              <>
                {' · via '}
                <span className="font-medium">{detail.account.verifiedName}</span>
              </>
            )}
            {detail.assignedAgent && (
              <>
                {' · '}
                Assigned to <span className="font-medium">{detail.assignedAgent.name}</span>
              </>
            )}
          </div>
        </div>
        <button
          onClick={onToggleContactPanel}
          className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
          title={contactPanelOpen ? 'Collapse contact panel' : 'Show contact panel'}
          aria-label="Toggle contact panel"
          aria-pressed={contactPanelOpen}
          type="button"
        >
          <ChevronRight
            className={`h-5 w-5 transition-transform ${contactPanelOpen ? 'rotate-180' : ''}`}
          />
        </button>
      </header>

      {/* Body: messages */}
      <div className="flex-1 overflow-hidden">
        <MessageList messages={detail.messages} notes={detail.internalNotes} />
      </div>

      {/* Composer */}
      <MessageComposer
        conversation={detail}
        onSent={refresh}
        onNoteAdded={refresh}
      />
    </div>
  );
}
